import {
  DEVICE_PROPERTIES,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  EVENT_SEVERITY
} from '#~/analytics/constants/index.js';
import type { DiscoveryEmptyProperties, EventProperties } from '#~/analytics/types/properties.js';
import type { AnalyticsTrackFn } from './types.js';

const SESSION_DEDUPE_KEY = 'analytics.discovery.session.dedupe';

const readSessionDedupeKeys = (): Set<string> => {
  if (typeof window === 'undefined') {
    return new Set();
  }

  try {
    const raw = window.sessionStorage.getItem(SESSION_DEDUPE_KEY);

    if (!raw) {
      return new Set();
    }

    const parsed = JSON.parse(raw) as string[];

    if (!Array.isArray(parsed)) {
      return new Set();
    }

    return new Set(parsed);
  } catch {
    return new Set();
  }
};

const writeSessionDedupeKeys = (keys: Set<string>): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(SESSION_DEDUPE_KEY, JSON.stringify(Array.from(keys)));
  } catch {
    // Ignore storage failures.
  }
};

const shouldEmitSessionScoped = (dedupeKey: string): boolean => {
  const keys = readSessionDedupeKeys();

  if (keys.has(dedupeKey)) {
    return false;
  }

  keys.add(dedupeKey);
  writeSessionDedupeKeys(keys);

  return true;
};

/** Options controlling the first-stream check on media_stream_succeeded. */
export interface MediaStreamingSuccessOptions {
  // Whether media_stream_succeeded may fire on this call. The room observer sets false on
  // reconnects after it has recorded the first stream.
  emitCanonical?: boolean;
  // Session-absolute guard key, `${device_id}:${media_type}`. When provided (and
  // emitCanonical is true) the canonical fires at most once per key for the whole browser
  // session. This is the room observer's committed-device guard: it survives observer
  // unmount/remount for the same committed device (the per-channel fired-ref resets on
  // remount and would otherwise re-fire the canonical). ENG-4313 B1: this key is NOT shared
  // with the add_device/preview surface — that surface keys on a TRANSIENT preview id that
  // never equals the committed id, so it does not emit the canonical at all (discovery.tsx
  // passes emitCanonical:false) and the committed-id room observer is the single canonical
  // source.
  canonicalDedupeKey?: string;
}

export const createMediaControlsDiscoveryTracker = (track: AnalyticsTrackFn) => ({
  trackMediaConnectSuccess: (properties: EventProperties = {}): void => {
    // Historical legacy rows remain available to readers; new connections emit once.
    track(EVENT_NAMES.MEDIA_CONNECT_SUCCEEDED, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackMediaConnectError: (properties: EventProperties = {}): void => {
    // Per think-in-systems (ai.knowledge/global), retire the alias at this shared sender.
    // Historical legacy rows remain available to compatibility readers in PostHog.
    track(EVENT_NAMES.MEDIA_CONNECT_FAILED, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackMediaStreamingSuccess: (
    properties: EventProperties = {},
    options: MediaStreamingSuccessOptions = {}
  ): void => {
    const { emitCanonical = true, canonicalDedupeKey } = options;

    // Keep first-stream meaning across reconnects and observer remounts for one committed device.
    const canonicalAllowed =
      emitCanonical &&
      (canonicalDedupeKey === undefined ||
        shouldEmitSessionScoped(`${EVENT_NAMES.MEDIA_STREAM_SUCCEEDED}:${canonicalDedupeKey}`));

    if (canonicalAllowed) {
      track(EVENT_NAMES.MEDIA_STREAM_SUCCEEDED, EVENT_CATEGORIES.DEVICE, properties);
    }
  },

  trackMediaStreamingError: (properties: EventProperties = {}): void => {
    // Historical legacy rows remain available to compatibility readers in PostHog.
    track(EVENT_NAMES.MEDIA_STREAM_FAILED, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackMediaQualityPing: (properties: EventProperties = {}): void => {
    // The legacy quality-ping alias completed its no-loss PostHog migration. New builds emit
    // one canonical heartbeat; deprecated catalog metadata keeps historical readers legible.
    // Per rule: stay-in-scope (ai.knowledge/global) — retire only this legacy alias.
    track(EVENT_NAMES.MEDIA_QUALITY_PING, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackControlsConnectSuccess: (properties: EventProperties = {}): void => {
    // Historical legacy rows remain available to compatibility readers in PostHog.
    track(EVENT_NAMES.CONTROLS_CONNECT_SUCCEEDED, EVENT_CATEGORIES.DEVICE, properties);
  },

  trackControlsConnectError: (properties: EventProperties = {}): void => {
    // Historical rows stay queryable; one canonical send prevents new duplicate failures.
    track(EVENT_NAMES.CONTROLS_CONNECT_FAILED, EVENT_CATEGORIES.DEVICE, properties);
  },

  // ── ENG-4312 stage-6 discovery telemetry ─────────────────────────────────
  //
  // ENG-4579 and ENG-4580 retire both discovery aliases after permanent readers
  // were proven over both names. The canonical events keep their shipped dedupe,
  // properties, and severity while historical PostHog rows remain unchanged.

  trackDiscoveryNoDevicesFound: (
    properties: DiscoveryEmptyProperties,
    sessionScopeKey: string = 'default'
  ): void => {
    // Canonical-only sender with the same per-surface scope, stamped
    // severity S3/warn: an empty discovery pass is an EXPECTED early-journey
    // state ("no cameras found yet"), not a failure, and alert rules MUST be
    // able to exclude it from blocking-spike semantics on this property alone
    // (ENG-4340 map: discovery_empty severity note).
    const canonicalDedupeKey = `${EVENT_NAMES.DISCOVERY_EMPTY}:${sessionScopeKey}`;

    if (shouldEmitSessionScoped(canonicalDedupeKey)) {
      track(EVENT_NAMES.DISCOVERY_EMPTY, EVENT_CATEGORIES.DEVICE, {
        ...properties,
        severity: EVENT_SEVERITY.S3_WARN
      });
    }
  },

  trackDiscoveryDeviceFound: (properties: EventProperties = {}, sessionScopeKey: string): void => {
    // Per the no-data-loss PostHog migration runbook, historical
    // device_discovery_device_found rows stay in PostHog and compatibility
    // readers keep both names. New builds send only the canonical event.
    // The canonical dedupe keys on device_id alone so one
    // physical camera mints exactly one discovery_device_found per session no
    // matter how many surfaces (background_discovery, video_source_selector)
    // re-announce it. Explicit string/empty check per rule: no implicit
    // truthiness. The defensive sessionScopeKey fallback keeps a (never
    // expected) device_id-less call deduped at legacy granularity instead of
    // spamming unkeyed events.
    const deviceId = properties[DEVICE_PROPERTIES.DEVICE_ID_CANONICAL];
    const canonicalScope =
      typeof deviceId === 'string' && deviceId !== '' ? deviceId : sessionScopeKey;
    const canonicalDedupeKey = `${EVENT_NAMES.DISCOVERY_DEVICE_FOUND}:${canonicalScope}`;

    if (shouldEmitSessionScoped(canonicalDedupeKey)) {
      track(EVENT_NAMES.DISCOVERY_DEVICE_FOUND, EVENT_CATEGORIES.DEVICE, properties);
    }
  },

  // ── ENG-4322 stage-6 discovery_mismatch (net-new, single emit) ───────────
  //
  // Unlike the two trackers above, this is NOT a dual-emit: discovery_mismatch
  // fires zero today (ENG-4340 map gap 12), so there is no legacy name to
  // migrate away from. The caller (Selector's discovery effect) owns the
  // per-host dedupe via emitDiscoveryMismatches/createDiscoveryMismatchDedupeState
  // in video/components/add/source/discovery-mismatch.ts — this tracker just
  // ships one already-decided emission.
  trackDiscoveryMismatch: (properties: EventProperties = {}): void => {
    track(EVENT_NAMES.DISCOVERY_MISMATCH, EVENT_CATEGORIES.DEVICE, properties);
  }
});
